import React, { type ChangeEvent, useCallback, useEffect, useState } from "react";
import { Button, DatePicker, Flex, RadioButton, RadioGroup, Text } from "@jsq/ds/core";
import { FilterPopover } from "./FilterPopover";
import {
  datePickerLabel,
  datePickerRow,
  filterPanelFooter,
  radioGroupWrap,
} from "./styles_vanilla.css";
import type { AuditLogDateFilter, AuditLogDateFilterMode } from "../types";

type Props = {
  value: AuditLogDateFilter | null;
  onChange: (filter: AuditLogDateFilter | null) => void;
};

const MODE_LABELS: Record<AuditLogDateFilterMode, string> = {
  equals: "Equals",
  after: "After",
  before: "Before",
  between: "Between",
};

const startOfDay = (date: Date): Date => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const endOfDay = (date: Date): Date => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
};

const buildFilterLabel = (filter: AuditLogDateFilter | null): string => {
  if (!filter) return "Date";
  const fmt = (iso: string) =>
    new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  if (filter.mode === "equals" && filter.dateFrom) return `On: ${fmt(filter.dateFrom)}`;
  if (filter.mode === "after" && filter.dateFrom) return `After: ${fmt(filter.dateFrom)}`;
  if (filter.mode === "before" && filter.dateTo) return `Before: ${fmt(filter.dateTo)}`;
  if (filter.mode === "between" && filter.dateFrom && filter.dateTo) {
    return `${fmt(filter.dateFrom)} – ${fmt(filter.dateTo)}`;
  }
  return "Date";
};

export const DateFilter: React.FC<Props> = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<AuditLogDateFilterMode>(value?.mode ?? "equals");
  const [dateFrom, setDateFrom] = useState<Date | null>(
    value?.dateFrom ? new Date(value.dateFrom) : null,
  );
  const [dateTo, setDateTo] = useState<Date | null>(
    value?.dateTo ? new Date(value.dateTo) : null,
  );

  const handleApply = useCallback(() => {
    let filter: AuditLogDateFilter;

    switch (mode) {
      case "equals":
        if (!dateFrom) return;
        filter = {
          mode,
          dateFrom: startOfDay(dateFrom).toISOString(),
          dateTo: endOfDay(dateFrom).toISOString(),
        };
        break;
      case "after":
        if (!dateFrom) return;
        filter = { mode, dateFrom: startOfDay(dateFrom).toISOString() };
        break;
      case "before":
        if (!dateTo) return;
        filter = { mode, dateTo: endOfDay(dateTo).toISOString() };
        break;
      case "between":
        if (!dateFrom || !dateTo) return;
        filter = {
          mode,
          dateFrom: startOfDay(dateFrom).toISOString(),
          dateTo: endOfDay(dateTo).toISOString(),
        };
        break;
    }

    onChange(filter);
    setIsOpen(false);
  }, [mode, dateFrom, dateTo, onChange]);

  const handleClear = useCallback(() => {
    setMode("equals");
    setDateFrom(null);
    setDateTo(null);
    onChange(null);
    setIsOpen(false);
  }, [onChange]);

  const handleModeChange = useCallback((newMode: AuditLogDateFilterMode) => {
    setMode(newMode);
    if (newMode === "before") {
      setDateFrom(null);
    } else if (newMode !== "between") {
      setDateTo(null);
    }
  }, []);

  useEffect(() => {
    if (value == null) {
      setMode("equals");
      setDateFrom(null);
      setDateTo(null);
    }
  }, [value]);

  const isApplyDisabled =
    (mode === "before" && !dateTo) ||
    (mode !== "before" && !dateFrom) ||
    (mode === "between" && !dateTo);

  return (
    <FilterPopover
      label={buildFilterLabel(value)}
      isActive={value != null}
      isOpen={isOpen}
      onToggle={() => setIsOpen((o) => !o)}
      onClose={() => setIsOpen(false)}
    >
      <div className={radioGroupWrap}>
        <RadioGroup
          name="date-filter-mode"
          value={mode}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            handleModeChange(e.target.value as AuditLogDateFilterMode)
          }
        >
          {(Object.keys(MODE_LABELS) as AuditLogDateFilterMode[]).map((m) => (
            <RadioButton key={m} value={m}>
              {MODE_LABELS[m]}
            </RadioButton>
          ))}
        </RadioGroup>
      </div>

      {mode !== "before" && (
        <div className={datePickerRow}>
          <Text variant="strong-sm" className={datePickerLabel}>
            {mode === "between" ? "From" : "Date"}
          </Text>
          <DatePicker
            selected={dateFrom}
            onChange={(date) => setDateFrom(date)}
            hasIcon
            isFull
            placeholderText="MM/DD/YYYY"
          />
        </div>
      )}

      {(mode === "before" || mode === "between") && (
        <div className={datePickerRow}>
          <Text variant="strong-sm" className={datePickerLabel}>
            {mode === "between" ? "To" : "Date"}
          </Text>
          <DatePicker
            selected={dateTo}
            onChange={(date) => setDateTo(date)}
            hasIcon
            isFull
            placeholderText="MM/DD/YYYY"
            maxDate={mode === "before" ? new Date() : undefined}
            minDate={mode === "between" ? (dateFrom ?? undefined) : undefined}
          />
        </div>
      )}

      <Flex justifyContent="space-between" alignItems="center" className={filterPanelFooter}>
        <Button displayAsLink isInline onClick={handleClear}>
          <Text variant="regular-sm">Clear</Text>
        </Button>
        <Button variant="primary" onClick={handleApply} disabled={isApplyDisabled}>
          Apply
        </Button>
      </Flex>
    </FilterPopover>
  );
};
